import React, { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import { FiEdit2, FiPlus, FiFilter, FiRotateCcw, FiImage } from "react-icons/fi";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useLocation } from "react-router-dom";
import { homeScreenData } from "./useHook";
import Swal from "sweetalert2";
import { usePermission, SystemPermissions } from "../../Utils/permissionAccess";

function DataManagement() {
  const {
    getHomeScreenData,
    postHomeScreenData,
    updateHomeScreenData,
    deleteHomeScreenData,
    getHomeScreenTypes,
    getHomeScreenSubTypes,
  } = homeScreenData();

  const location = useLocation();
  const { value: originalValue } = location.state || {};

  const valueMapping = {
    ourProjects: "projects",
    estimations: "construction",
  };

  const value = valueMapping[originalValue] || originalValue;

  const { hasPermission } = usePermission();

  const permissionMap = {
    houseDesign: {
      create: SystemPermissions.HomeScreenHouseDesign.CREATE,
      update: SystemPermissions.HomeScreenHouseDesign.CREATE,
      delete: SystemPermissions.HomeScreenHouseDesign.DELETE,
    },
    frontElevation: {
      create: SystemPermissions.HomeScreenHouseDesign.CREATE,
      update: SystemPermissions.HomeScreenHouseDesign.CREATE,
      delete: SystemPermissions.HomeScreenHouseDesign.DELETE,
    },
    projects: {
      create: SystemPermissions.HomeScreenConsturctionOurProjects.CREATE,
      update: SystemPermissions.HomeScreenConsturctionOurProjects.CREATE,
      delete: SystemPermissions.HomeScreenConsturctionOurProjects.DELETE,
    },
    blog: {
      create: SystemPermissions.HomeScreenConsturctionBlogs.CREATE,
      update: SystemPermissions.HomeScreenConsturctionBlogs.CREATE,
      delete: SystemPermissions.HomeScreenConsturctionBlogs.DELETE,
    },
    interiorDesign: {
      create: SystemPermissions.HomeScreenInteriorDesign.CREATE,
      update: SystemPermissions.HomeScreenInteriorDesign.CREATE,
      delete: SystemPermissions.HomeScreenInteriorDesign.DELETE,
    },
    shop: {
      create: SystemPermissions.HomeScreenConsturctionShop.CREATE,
      update: SystemPermissions.HomeScreenConsturctionShop.CREATE,
      delete: SystemPermissions.HomeScreenConsturctionShop.DELETE,
    },
    construction: {
      create: SystemPermissions.HomeScreenConsturctionCalculaotr.CREATE,
      update: SystemPermissions.HomeScreenConsturctionCalculaotr.CREATE,
      delete: SystemPermissions.HomeScreenConsturctionCalculaotr.DELETE,
    },
  };

  const categoryPermissions = permissionMap[value] || {};
  const canCreate = categoryPermissions.create
    ? hasPermission(categoryPermissions.create)
    : true;
  const canUpdate = categoryPermissions.update
    ? hasPermission(categoryPermissions.update)
    : canCreate;
  const canDelete = categoryPermissions.delete
    ? hasPermission(categoryPermissions.delete)
    : true;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [linkValue, setLinkValue] = useState("");
  const [homeScreen, setHomeScreen] = useState([]);
  const [types, setTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSubTypeId, setSelectedSubTypeId] = useState("");
  const [filterTypeId, setFilterTypeId] = useState("");
  const [appliedFilterTypeId, setAppliedFilterTypeId] = useState("");

  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getHomeScreenData(setHomeScreen);
    const fetchTypes = async () => {
      const t = await getHomeScreenTypes();
      setTypes(t);
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    if (selectedTypeId) {
      const fetchSub = async () => {
        const st = await getHomeScreenSubTypes(selectedTypeId);
        setSubTypes(st);
      };
      fetchSub();
    } else {
      setSubTypes([]);
    }
  }, [selectedTypeId]);

  const openAddModal = () => {
    setEditingItem(null);
    setLinkValue("");
    setSelectedImage(null);
    setImagePreview(null);
    setImageError("");
    setSelectedTypeId("");
    setSelectedSubTypeId("");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setLinkValue(item.link || "");
    setSelectedImage(null);
    setImagePreview(item.image || null);
    setImageError("");
    setSelectedTypeId(item.type?.id?.toString() || "");
    setSelectedSubTypeId(item.subType?.id?.toString() || "");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setLinkValue("");
    setImagePreview(null);
    setSelectedImage(null);
    setImageError("");
    setSelectedTypeId("");
    setSelectedSubTypeId("");
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 800 && img.height === 500) {
          resolve(true);
        } else {
          reject(
            new Error(
              `Image dimensions must be exactly 800x500 pixels. Current dimensions: ${img.width}x${img.height}`,
            ),
          );
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    setImageError("");

    if (!file.type.startsWith("image/")) {
      setImageError("Please select a valid image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size must be less than 5MB.");
      return;
    }

    try {
      await validateImageDimensions(file);
      setSelectedImage(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setImageError(error.message);
      setSelectedImage(null);
      setImagePreview(editingItem?.image || null);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(editingItem?.image || null);
    setImageError("");
  };

  const handleSubmit = async () => {
    if (!linkValue?.trim()) {
      Swal.fire("Error!", "Link is required.", "error");
      return;
    }

    if (!selectedTypeId) {
      Swal.fire("Error!", "Type is required.", "error");
      return;
    }

    const selectedTypeObj = types.find(t => t.id.toString() === selectedTypeId);
    const requiresSubType = selectedTypeObj && ['architecture', 'interior', 'contracts'].includes(selectedTypeObj.name.toLowerCase());

    if (requiresSubType && !selectedSubTypeId) {
      Swal.fire("Error!", "Sub Type is required.", "error");
      return;
    }

    if (!editingItem && !selectedImage) {
      setImageError("Image is required.");
      return;
    }

    const payload = {
      link: linkValue.trim(),
      typeId: selectedTypeId,
      subTypeId: selectedSubTypeId,
    };

    if (selectedImage) {
      payload.image = selectedImage;
    }

    setIsSubmitting(true);
    try {
      if (editingItem) {
        const response = await updateHomeScreenData(editingItem.id, payload);
        if (response && response.success === false) {
          throw new Error(response.message || "Failed to update item.");
        }
        Swal.fire(
          "Success!",
          response?.message || "Item updated successfully.",
          "success",
        );
      } else {
        const response = await postHomeScreenData(payload);
        if (response && response.success === false) {
          throw new Error(response.message || "Failed to add item.");
        }
        Swal.fire(
          "Success!",
          response?.message || "Item added successfully.",
          "success",
        );
      }

      await getHomeScreenData(setHomeScreen);
      closeModal();
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error!",
        error?.message || "Something went wrong.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteHomeScreenData(id);
        if (response && response.success === false) {
          throw new Error(response.message || "Failed to delete item.");
        }
        await getHomeScreenData(setHomeScreen);
        Swal.fire(
          "Deleted!",
          response?.message || "Your item has been deleted.",
          "success",
        );
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Error!",
          error?.message || "Something went wrong.",
          "error",
        );
      }
    }
  };

  // Convert legacy homeScreen object or use array directly
  let categoryData = [];
  if (Array.isArray(homeScreen)) {
    categoryData = homeScreen;
  } else if (Array.isArray(homeScreen?.data)) {
    categoryData = homeScreen.data;
  }

  if (appliedFilterTypeId && appliedFilterTypeId !== "all") {
    categoryData = categoryData.filter(
      (item) => item.type?.id?.toString() === appliedFilterTypeId
    );
  }

  const isEditMode = Boolean(editingItem);

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Home Screen Management</h2>

        {canCreate && (
          <button onClick={openAddModal} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add New Entry
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <select
          className="status-select"
          value={filterTypeId}
          onChange={(e) => setFilterTypeId(e.target.value)}
        >
          <option value="" className="opacity-25">Select Type</option>
          {types.map((t) => (
            <option key={t.id} value={t.id.toString()}>
              {t.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={() => setAppliedFilterTypeId(filterTypeId)}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={() => {
              setFilterTypeId("");
              setAppliedFilterTypeId("");
            }}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow my-5">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Image</th>
                <th className="table-th">Type</th>
                <th className="table-th">Sub Type</th>
                <th className="table-th">Link</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categoryData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-gray-500">
                    No data found
                  </td>
                </tr>
              ) : (
                categoryData.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <img
                        src={item.image}
                        alt="Category"
                        className="h-16 w-16 object-cover rounded shadow"
                      />
                    </td>
                    <td className="table-td max-w-[200px]">
                      {item.type?.name}
                    </td>
                    <td className="table-td">
                      {item.subType?.name}
                    </td>
                    <td className="table-td max-w-[200px] truncate">
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {item.link}
                      </a>
                    </td>
                    <td className="table-td flex space-x-1">
                      {canUpdate && (
                        <button
                          className="btn btn-edit text-white"
                          onClick={() => openEditModal(item)}
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn btn-delete text-white"
                          onClick={() => handleDelete(item.id)}
                        >
                          <MdDelete className="text-white" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {isEditMode ? "Edit Entry" : "Add Entry"}
              </h2>
              <XMarkIcon
                className="h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                onClick={closeModal}
              />
            </div>

            <div>
              <div className="space-y-3 mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Image {!isEditMode && <span className="text-red-500">*</span>}
                </label>

                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />

                {imagePreview ? (
                  <div className="group relative w-full overflow-hidden rounded-xl border border-gray-200 bg-stone-50 shadow-sm">
                    <div className="aspect-[8/5] w-full">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-full w-full object-contain"
                      />
                    </div>

                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-3 pb-3 pt-10">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-white">
                          {selectedImage
                            ? selectedImage.name
                            : isEditMode
                              ? "Current image"
                              : "Selected image"}
                        </p>
                        <p className="text-[11px] text-white/75">
                          800 × 500 · max 5MB
                        </p>
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <label
                          htmlFor="image-upload"
                          className="btn btn-edit text-white h-9 cursor-pointer px-3 text-sm"
                          title={isEditMode ? "Change image" : "Replace image"}
                        >
                          <FiImage className="text-white" />
                          {isEditMode ? "Change Image" : "Replace Image"}
                        </label>

                        {selectedImage && (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="btn btn-delete text-white h-9 px-3 text-sm"
                            title="Remove selected image"
                          >
                            <TrashIcon className="h-4 w-4 text-white" />
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload"
                    className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
                  >
                    <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                      <CameraIcon className="h-6 w-6 text-[#982598]" />
                    </span>
                    <span className="font-medium text-gray-700">
                      Upload Image
                    </span>
                    <span className="mt-1 text-sm text-gray-400">
                      Click to browse files
                    </span>
                    <span className="mt-2 rounded-full bg-white px-3 py-1 text-xs text-gray-500 shadow-sm">
                      Required: 800 × 500 px
                    </span>
                  </label>
                )}

                {imageError && (
                  <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {imageError}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Image must be exactly 800×500 pixels and less than 5MB
                  {isEditMode
                    ? ". Leave unchanged to keep the current image."
                    : ""}
                </p>
              </div>

              <div className="mb-4">
                <label className="block mb-2 font-semibold">
                  Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {types.map((type) => (
                    <label key={type.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type"
                        value={type.id}
                        checked={selectedTypeId === type.id.toString()}
                        onChange={(e) => {
                          setSelectedTypeId(e.target.value);
                          setSelectedSubTypeId(""); // reset subtype when type changes
                        }}
                        className="form-radio text-blue-600 cursor-pointer"
                      />
                      <span>{type.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(() => {
                const selectedTypeObj = types.find(t => t.id.toString() === selectedTypeId);
                const requiresSubType = selectedTypeObj && ['architecture', 'interior', 'contracts'].includes(selectedTypeObj.name.toLowerCase());
                
                return requiresSubType && subTypes.length > 0 && (
                  <div className="mb-4">
                    <label className="block mb-2 font-semibold">
                      Sub Type <span className="text-red-500">*</span>
                    </label>
                  <div className="grid grid-cols-2 gap-2">
                    {subTypes.map((sub) => (
                      <label key={sub.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="subType"
                          value={sub.id}
                          checked={selectedSubTypeId === sub.id.toString()}
                          onChange={(e) => setSelectedSubTypeId(e.target.value)}
                          className="form-radio text-blue-600 cursor-pointer"
                        />
                        <span>{sub.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                );
              })()}

              <label className="block mb-2 font-semibold">
                Link <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="link"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
                className="w-full mb-4 p-2 border rounded bg-gray-200 h-10"
                placeholder="Enter link..."
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="btn btn-reset-filter justify-center"
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn justify-center ${
                  isEditMode ? "btn-edit text-white" : "btn-primary"
                } ${
                  isSubmitting || (!isEditMode && !selectedImage)
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={handleSubmit}
                disabled={isSubmitting || (!isEditMode && !selectedImage)}
              >
                {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataManagement;
